import { useState } from "react";
import useSWR from "swr";
import { toast } from "sonner";
import { Cloud, Download, Film, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, fetcher } from "@/lib/api";

export const VideoPanel = ({ articleId, outlets, outletId, videos, onBody, onVideos }) => {
  const [url, setUrl] = useState("");
  const [found, setFound] = useState([]);
  const [caption, setCaption] = useState("");
  const [credit, setCredit] = useState("");
  const [position, setPosition] = useState("top");
  const [libQuery, setLibQuery] = useState("");
  const { data: library } = useSWR(
    `/videos/library?q=${encodeURIComponent(libQuery)}&limit=12`, fetcher);
  const [outlet, setOutlet] = useState(outletId || (outlets || [])[0]?.id || "");
  const [busy, setBusy] = useState("");

  const detect = async () => {
    setBusy("detect");
    setFound([]);
    try {
      const { data } = await api.post("/videos/detect", { url });
      setFound(data.videos || []);
      toast.success(`${data.videos.length} video(s) encontrado(s)`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  const add = async (video, mode) => {
    setBusy((video.url || video.id) + mode);
    try {
      const { data } = await api.post(`/articles/${articleId}/video`, {
        url: video.url || "", video_id: video.id || null, mode, caption, credit, position,
        outlet_id: outlet || null,
      });
      onBody(data.body_html);
      onVideos?.(data.videos || []);
      toast.success(mode === "upload" ? "Video subido a WordPress e insertado"
                                      : "Video insertado en la nota");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="video-panel">
      <div className="kicker mb-1 flex items-center gap-2">
        <Film className="h-3.5 w-3.5" /> Videos de la nota
      </div>
      <p className="text-xs text-muted-foreground mb-5">
        Pegue el enlace de la nota de origen o del video (YouTube, Facebook, Vimeo o archivo MP4).
        Puede incrustarlo tal cual o descargarlo y subirlo a la biblioteca de su WordPress.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input value={url} onChange={(e) => setUrl(e.target.value)}
               placeholder="https://… enlace de la nota o del video"
               data-testid="video-url-input" />
        <Button onClick={detect} disabled={!url || busy === "detect"} data-testid="video-detect-btn">
          <Search className="h-4 w-4 mr-2" />{busy === "detect" ? "Buscando…" : "Buscar video"}
        </Button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <div>
          <Label className="text-xs uppercase tracking-wider">Pie del video</Label>
          <Input value={caption} onChange={(e) => setCaption(e.target.value)} className="mt-1.5"
                 data-testid="video-caption-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Crédito o fuente</Label>
          <Input value={credit} onChange={(e) => setCredit(e.target.value)} className="mt-1.5"
                 data-testid="video-credit-input" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Posición en la nota</Label>
          <select value={position} onChange={(e) => setPosition(e.target.value)}
                  className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
                  data-testid="video-position-select">
            <option value="top">Al inicio del cuerpo (destacado)</option>
            <option value="bottom">Al final del cuerpo</option>
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Medio (para subir el archivo)</Label>
          <select value={outlet} onChange={(e) => setOutlet(e.target.value)}
                  className="w-full mt-1.5 h-10 border border-input bg-background px-3 text-sm rounded-md"
                  data-testid="video-outlet-select">
            {(outlets || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      </div>

      {found.length > 0 && (
        <div className="space-y-3" data-testid="video-results">
          {found.map((v, i) => (
            <div key={v.url} className="border border-border p-4" data-testid={`video-found-${i}`}>
              <p className="text-sm font-medium">{v.provider}</p>
              <p className="text-xs text-muted-foreground break-all mb-3">{v.url}</p>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => add(v, "embed")}
                        disabled={busy === v.url + "embed"} data-testid={`video-embed-${i}`}>
                  <Film className="h-3.5 w-3.5 mr-1.5" />
                  {busy === v.url + "embed" ? "Insertando…" : "Incrustar en la nota"}
                </Button>
                {v.kind === "file" && (
                  <Button size="sm" variant="outline" onClick={() => add(v, "upload")}
                          disabled={busy === v.url + "upload"} data-testid={`video-upload-${i}`}>
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    {busy === v.url + "upload" ? "Subiendo…" : "Subir a WordPress"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-7 border-t border-border pt-5" data-testid="video-library-picker">
        <div className="kicker mb-1 flex items-center gap-2">
          <Cloud className="h-3.5 w-3.5 text-primary" />Videos de la agencia
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Los videos subidos en Biblioteca → Videos, con su pie, crédito y carátula. Puede
          enlazarlos desde la nube de la agencia o subirlos a la biblioteca de WordPress.
        </p>
        <Input value={libQuery} onChange={(e) => setLibQuery(e.target.value)}
               placeholder="Buscar en la videoteca" className="mb-3"
               data-testid="video-library-search" />
        {!library?.items?.length ? (
          <p className="text-xs text-muted-foreground">No hay videos en la videoteca.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {library.items.map((v) => (
              <div key={v.id} className="border border-border p-3" data-testid={`video-lib-${v.id}`}>
                <video src={v.url} poster={v.poster_url || undefined} controls preload="none"
                       className="w-full aspect-video bg-black mb-2" />
                <p className="text-sm font-medium truncate" title={v.title}>{v.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-2">{v.caption}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => add(v, "link")}
                          disabled={busy === v.id + "link"} data-testid={`video-lib-link-${v.id}`}>
                    {busy === v.id + "link" ? "Insertando…" : "Insertar desde la nube"}
                  </Button>
                  <Button size="sm" onClick={() => add(v, "upload")}
                          disabled={busy === v.id + "upload"} data-testid={`video-lib-upload-${v.id}`}>
                    {busy === v.id + "upload" ? "Subiendo…" : "Subir a WordPress"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {(videos || []).length > 0 && (
        <div className="mt-6" data-testid="video-added-list">
          <div className="kicker mb-2">Videos ya añadidos</div>
          <ul className="text-xs text-muted-foreground space-y-1">
            {videos.map((v) => (
              <li key={v.src} className="break-all">
                {v.kind === "embed" ? "Incrustado" : "Archivo"} · {v.src}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
